import {css} from '@emotion/react';
import styled from '@emotion/styled';

import {
  mixinOverflow,
  mixinSVGIconSmall,
  mixinTruncate,
} from '@ELib/styles/mixins';

const mixinTableHead = css`
  font-size: var(--font-size-tiny);
  font-weight: var(--table-header-font-weight);
  white-space: nowrap;
  line-height: var(--table-header-line-height);
`;

export const StyledETable = styled.table`
  border-collapse: collapse;
  width: 100%;
  user-select: none;

  // while the body is virtualized the measured column widths are applied
  // through a <colgroup>; the fixed layout makes the browser keep them instead
  // of deriving the widths again from the content of the mounted rows
  &.stableColumnWidths {
    table-layout: fixed;

    // the drag count badge of a dragged row reaches out of its cell, so it
    // must keep hanging over the edge
    td:not(.countInfo) {
      // content wider than its column can no longer widen the column, so cut
      // it off instead of letting it reach into the next one
      overflow: hidden;
    }

    // a cell that is only as wide as the content of the measured rows can be
    // too narrow for a later row, so mark the cut instead of ending the text
    // mid-character
    td.modeMin {
      text-overflow: ellipsis;
    }
  }

  &.single-line-header tbody tr {
    scroll-margin-top: var(--table-header-single-line-height);
  }

  &.double-line-header tbody tr {
    scroll-margin-top: var(--table-header-double-line-height);
  }

  thead {
    position: sticky;
    top: 0;
    background: var(--color-core-white);
    z-index: 1; // ensure that expansion buttons, which have their own stacking context, are covered
  }

  tr {
    height: var(--table-row-height);

    td {
      transition:
        background-color var(--animation-fade-in-out-duration),
        color var(--animation-fade-in-out-duration);

      svg {
        transition: fill var(--animation-fade-in-out-duration);
      }
    }
  }

  // spacer rows that keep the scroll geometry when the body is virtualized
  tbody > tr.virtualSpacer {
    height: auto;

    td {
      padding: 0;
      border: none;
    }
  }

  tbody > tr {
    &.styleInfo td {
      color: var(--color-core-dark-grey);
      font-size: var(--font-size-tiny);
      font-weight: var(--font-weight-thin);
    }

    &.styleHeader td {
      ${mixinTableHead}
    }

    &.styleImportant td {
      color: var(--color-alerts-error);
    }

    &.styleWarning td:not(.selection) {
      background-color: var(--color-notification-resolvable);
    }

    &.styleDanger {
      td:not(.selection) {
        background-color: var(--color-notification-important);
      }

      &:not(.selected):not(.active) {
        td {
          color: var(--color-core-white);

          &.expansion svg,
          &.extraActions .HeaderLabel svg,
          .SmallIconCell {
            fill: var(--color-core-white);
          }
        }
      }
    }

    &.styleHiddenBorderBottom td:not(.styleTotal) {
      // styleHiddenBorderBottom can be set on the row or on certain cells only.
      // Row-level blanks are necessary to also apply the styling to the expansion cell.
      border-bottom-color: transparent;
    }

    &.styleDisabled {
      :not(.active):not(.selected):not(:hover) {
        color: var(--table-disabled-font-color);
        input,
        .ESelect {
          color: var(--table-disabled-font-color);
        }
      }
    }

    &.styleBold td {
      font-weight: var(--table-highlight-font-weight);
    }

    &.selected {
      td:not(.selection) {
        background: var(--color-state-selected);
      }

      .EIconButton {
        color: var(--color-core-black);
      }
    }

    &.active {
      td:not(.selection) {
        background: var(--color-state-active);
      }

      .EIconButton {
        color: var(--color-core-black);
      }
    }

    &:not(.noHover):hover {
      td:not(.selection) {
        background: var(--color-state-hover);
      }

      &:not(.selected):not(.active) {
        &.styleWarning td:not(.selection) {
          background-color: var(---color-notification-resolvable-hover);
        }

        &.styleDanger td:not(.selection) {
          background-color: var(---color-notification-important-hover);
        }
      }

      .EIconButton {
        color: var(--color-core-black);

        &:hover {
          color: var(--color-core-white);
        }
      }
    }

    &.styleDragged {
      opacity: var(--element-drag-opacity);

      &.styleGhost {
        cursor: grabbing;
        background: var(--color-core-white);

        td.countInfo {
          position: relative;

          &:before {
            content: attr(data-drag-count);
            position: absolute;
            top: -3px;
            left: 0px;
            border-radius: 100%;
            padding: var(--gutter-very-tiny);
            background-color: var(--color-notification-important);
            color: var(--color-core-white);
            font-size: var(--font-size-tiny);
            min-width: 18px;
            text-align: center;
          }
        }

        td.selection {
          visibility: hidden;
        }
      }

      td,
      td button {
        cursor: grabbing; // overwrites cursor setting of button
      }
    }
  }

  th {
    text-align: left;
    ${mixinTableHead};

    &.selection {
      padding: 0;
      width: 0;
    }

    &.header {
      > div {
        display: flex;
        gap: var(--gutter-tiny);
        align-items: center;
        width: max-content;
        padding: 5px var(--gutter-little);
        margin-bottom: var(--gutter-little);
      }

      &.alignCenter > div {
        margin-left: auto;
        margin-right: auto;
      }

      &.alignRight {
        text-align: right;

        > div {
          // headings that are sortable
          margin-left: auto;
        }
      }

      &.canSort {
        cursor: pointer;

        > div {
          width: 100%;
        }

        > div > span {
          ${mixinTruncate};
          width: 100%;
        }

        &:not(.isSorted) {
          > div {
            color: var(--color-core-link);
          }

          svg {
            // hide the sorting direction but keep it in the DOM to avoid reflow when sorting
            opacity: 0;
          }
        }

        &.isSorted > div {
          background: var(--color-state-active);
        }

        &.alignRight > div > span {
          // show sorting chevron on the left side to improve alignment of headings
          order: 2;
        }
      }

      svg {
        ${mixinSVGIconSmall}
      }
    }
  }

  // selection checkbox visibility
  & {
    th,
    td {
      &.selection {
        opacity: 0;
        transition: opacity var(--animation-fade-in-out-duration);
      }
    }

    &.hasSelection {
      th,
      td {
        &.selection {
          opacity: 1;
        }
      }
    }

    tr.forceCheckboxes {
      th,
      td {
        opacity: 1;
      }
    }

    tr:hover,
    tr.active {
      td.selection {
        opacity: 1;
      }
    }
  }

  th,
  td {
    &.extraActions {
      // hardcode a width to avoid styling issues where chrome would not adhere to the automatic
      // minimal width of certain columns.
      width: 48px;
    }

    &.expansion {
      // reserve the arrow button space (small icon 12px + icon button padding
      // 2 x 8px) even while no rendered row shows one — rows outside the
      // virtual window or in later pages can have an expansion
      min-width: 28px;
    }
  }

  td:not(.selection) {
    padding: 0 var(--gutter-little);
    border-bottom: var(--regular-border-width) solid var(--separator-color);
    border-top: var(--regular-border-width) solid var(--separator-color);
  }

  td {
    font-size: var(--font-size-small);
    font-weight: var(--font-weight-thin);

    // cell that contains a sub table
    &.subContainer {
      padding: var(--gutter-big) 0 var(--gutter-xlarge);
    }

    &.alignCenter {
      text-align: center;
    }

    &.textSelectable {
      user-select: text;
    }

    &.textSelectable::selection {
      background: white;
    }

    &.alignRight {
      text-align: right;
    }

    &.styleHiddenBorderBottom {
      border-bottom-color: transparent;
    }

    &.styleTotal {
      border-bottom: var(--separator-color) 4px double;
    }

    // make cell as narrow as possible without truncating/breaking any content
    &.modeMin {
      width: 0;
      white-space: nowrap;
    }

    // truncate long lines while (approximately) respecting their width definition
    &.modeTruncate {
      max-width: 1px;
      ${mixinTruncate};

      // if the inline element has a tooltip there will be a span which will not be correctly sized
      // this way we avoid the sizing problem and fix the tooltip position
      > span {
        display: block;
        ${mixinTruncate};
        max-width: fit-content;
      }
    }

    // break long content into multiple lines
    &.modeBreak {
      hyphens: auto;
      word-break: break-word; // break long words if there is a width specified
    }
  }

  // special icon cells
  tr {
    td:is(.expansion, .extraActions) svg {
      transition: fill var(--animation-fade-in-out-duration);
      fill: var(--color-core-link);
    }

    &:hover,
    &.selected,
    &.active {
      td:is(.expansion, .extraActions) svg {
        fill: var(--color-core-black);
      }
    }
  }

  // Remove margin from ECheckboxes
  .no-margin {
    margin: 0;
  }
`;

export const StyledETableScrollWrapper = styled.div`
  height: 100%;
  width: 100%;
  ${mixinOverflow}

  // while the body is virtualized the table compensates the scroll position
  // itself when the data window changes, and native browser scroll anchoring
  // on top of that would double-compensate. Without virtualization the native
  // anchoring keeps the rows in place for the changes the table does not
  // compensate itself, e.g. when an expansion above them collapses
  &.virtualizedBody {
    overflow-anchor: none;
  }
`;

export const StyledETableRefreshIndicator = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: var(--gutter-medium);
  align-items: center;
  position: sticky;
  cursor: pointer;
  width: min-content;
  white-space: nowrap;

  margin-left: auto;
  margin-right: auto;
  margin-top: var(--gutter-medium);

  padding: var(--gutter-small);

  bottom: var(--gutter-small);

  color: var(--button-content-color);
  background-color: var(--button-active-bg-color);

  border-radius: var(--reload-indicator-border-radius);

  box-shadow: 0 0 6px rgba(0, 0, 0, 0.25);

  &:hover {
    background-color: var(--button-hover-bg-color);
    border-color: var(--button-hover-bg-color);
  }

  .WrapperEIconButton > button {
    margin: -3.5px 0 0;
    padding: 0;
    svg {
      fill: var(--color-core-white);
    }
  }
`;
